/**
 * Bijective mapping between store field keys (`snake_case`, e.g.
 * `current_job_include_job_title`) and readable JSON keys (`camelCase`, e.g.
 * `currentJobIncludeJobTitle`), plus a curated override table for the few keys
 * that deserve a nicer name (see KEY_OVERRIDES).
 *
 * The transform is only used when it round-trips exactly
 * (`toSnakeKey(toCamelKey(k)) === k`); otherwise the raw store key is kept, so a
 * key can never be corrupted. `buildKeyMaps` derives the exact inverse used by
 * the parse layer from a scope's known field list, so present/parse stay perfect
 * inverses.
 */
import { KEY_OVERRIDES } from './constants';

/** `foo_bar_baz` -> `fooBarBaz` */
export function toCamelKey(snake: string): string {
  return snake.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}

/** `fooBarBaz` -> `foo_bar_baz` */
export function toSnakeKey(camel: string): string {
  return camel.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

/** True when the snake key survives a camel round-trip unchanged. */
export function keyRoundTrips(snake: string): boolean {
  return toSnakeKey(toCamelKey(snake)) === snake;
}

const REVERSE_OVERRIDES: Record<string, string> = Object.fromEntries(
  Object.entries(KEY_OVERRIDES).map(([storeKey, jsonKey]) => [
    jsonKey,
    storeKey,
  ])
);

/** The readable JSON key for a given store field key. */
export function storeKeyToJsonKey(storeKey: string): string {
  if (KEY_OVERRIDES[storeKey]) return KEY_OVERRIDES[storeKey];
  return keyRoundTrips(storeKey) ? toCamelKey(storeKey) : storeKey;
}

/**
 * The store field key for a readable JSON key — the exact inverse of
 * `storeKeyToJsonKey`. A raw (already-snake) key passes through unchanged because
 * `toSnakeKey` is the identity on lowercase input.
 */
export function jsonKeyToStoreKey(jsonKey: string): string {
  if (REVERSE_OVERRIDES[jsonKey]) return REVERSE_OVERRIDES[jsonKey];
  return toSnakeKey(jsonKey);
}
